import { useTranslation } from 'react-i18next';
import type { CaptiveContext } from '../api/captive';

/**
 * Read-only display of the connecting device's identity, as the core resolved it
 * from the captive lease (POST /captive/context). Reassures the subscriber that the
 * portal already knows which device it is registering — there is no device-entry
 * form (Amendment 02).
 *
 * ⚠ THE ADDRESS IS THE ONE ROW THAT IS NOT REASSURANCE *(customer request,
 * 2026-08-22)*. Everything else here is «we already know who you are»; `client_ip`
 * is an operational value a human reads out loud down a telephone line while a
 * support operator types it in. That is why it is rendered first, larger, and apart
 * from the identity rows rather than as a sixth row among them — and why it survives
 * when every other row is empty, which is the common case for the subscriber an
 * operator is actually phoning about (suspended, holding an ordinary public address;
 * or static, with no captive lease at all).
 *
 * ⚠ NO «COPY» BUTTON, DELIBERATELY. `navigator.clipboard` requires a secure context,
 * and the walled-garden `:80` DNAT serves this page over PLAIN HTTP — the button
 * would silently do nothing for exactly the population that needs it, which is worse
 * than no button. `user-select: all` on the value gives a single tap/click that
 * selects the whole address on both the http and the https route, and reading it
 * aloud (the stated use) needs no control at all.
 */
export function IdentityPanel({ context }: { context: CaptiveContext }) {
  const { t } = useTranslation();
  const rows: Array<[string, string | number | undefined]> = [
    [t('connection.mac'), context.mac],
    [t('connection.opt82Circuit'), context.opt82_circuit],
    [t('connection.opt82Remote'), context.opt82_remote],
    [
      t('connection.trap'),
      context.trap_switch
        ? context.trap_port
          ? `${context.trap_switch} / ${context.trap_port}`
          : context.trap_switch
        : undefined,
    ],
    [t('connection.vlan'), context.vlan ? context.vlan : undefined],
  ].filter(([, v]) => v !== undefined && v !== '') as Array<[string, string | number]>;

  // Defensive: an older core (or a mock written before 2026-08-22) sends no
  // `client_ip` at all, and `undefined` rendered into the address slot is the worst
  // possible outcome for a value a subscriber is asked to read out loud.
  const clientIp = (context.client_ip ?? '').trim();

  // Nothing at all to show: render no panel rather than an empty titled box. Reachable
  // when the core answers a context with neither an address nor an identity (an older
  // core that predates `client_ip`, say) — the caller then falls through to its muted
  // hint, exactly as it does for a failed read.
  if (clientIp === '' && rows.length === 0) return null;

  return (
    <div className="identity-panel" style={{ marginBottom: 20 }}>
      <span className="group-label">{t('connection.title')}</span>
      {clientIp !== '' && (
        <div className="identity-ip">
          <span className="k">{t('connection.clientIp')}</span>
          <span className="v mono ip">{clientIp}</span>
        </div>
      )}
      {rows.map(([k, v]) => (
        <div className="identity-row" key={k}>
          <span className="k">{k}</span>
          <span className="v mono">{v}</span>
        </div>
      ))}
      {clientIp !== '' && <p className="identity-note">{t('connection.ipNote')}</p>}
    </div>
  );
}
